import React from "react";
import { motion } from "framer-motion";
import { Mail, Calendar, ArrowRight } from "lucide-react";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 px-4 sm:px-6 bg-[#FFD800] border-y-[3px] border-[#0D0D0D] overflow-hidden"
    >
      <div className="absolute inset-0 halftone-bg pointer-events-none" />
      <motion.div
        className="absolute top-12 left-[6%] w-20 h-20 bg-[#00E5FF] brut-border brut-shadow rotate-12 hidden md:block"
        animate={{ rotate: [12, -10, 12] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-16 right-[8%] w-24 h-24 rounded-full bg-[#FF0055] brut-border brut-shadow hidden md:block"
        animate={{ y: [0, -16, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <span className="font-mono text-xs uppercase tracking-[0.3em]">
            [06] — Let's build
          </span>
          <h2 className="mt-3 font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
            Got a wild <br />
            idea?{" "}
            <span className="font-script normal-case text-[#FF0055]">
              Let's ship it.
            </span>
          </h2>
          <p className="mt-6 font-body text-[#0D0D0D]/85 max-w-2xl mx-auto text-lg leading-relaxed">
            Pick the channel that suits you — I read every message myself and
            reply within 24 hours.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <a
            href="mailto:incinkcreative@gmail.com"
            data-testid="contact-email-link"
            className="group flex flex-col gap-4 brut-border bg-white p-6 brut-shadow brut-lift"
          >
            <div className="w-12 h-12 bg-[#0D0D0D] text-[#FFD800] flex items-center justify-center brut-border">
              <Mail size={22} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                Email
              </div>
              <div className="font-display font-black uppercase text-l mt-1 break-all">
                incinkcreative@gmail.com
              </div>
            </div>
            <span className="mt-auto inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#FF0055] group-hover:gap-2 transition-all">
              Send email <ArrowRight size={14} />
            </span>
          </a>

          <a
            href="https://calendly.com/incinkcreative"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-calendar-link"
            className="group flex flex-col gap-4 brut-border bg-[#FF0055] text-white p-6 brut-shadow brut-lift"
          >
            <div className="w-12 h-12 bg-white text-[#FF0055] flex items-center justify-center brut-border">
              <Calendar size={22} />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/80">
                Book a call
              </div>
              <div className="font-display font-black uppercase text-xl mt-1">
                30 min — free intro
              </div>
            </div>
            <span className="mt-auto inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#FFD800] group-hover:gap-2 transition-all">
              Open Calendly <ArrowRight size={14} />
            </span>
          </a>

          <a
            href="https://wa.me/919766990448?text=Hi%20IncInk!%20I'd%20like%20to%20chat%20about%20a%20project."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-link"
            className="group flex flex-col gap-4 brut-border bg-[#00E5FF] p-6 brut-shadow brut-lift"
          >
            <div className="w-12 h-12 bg-[#25D366] text-white flex items-center justify-center brut-border">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/>
              </svg>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#0D0D0D]/70">
                WhatsApp
              </div>
              <div className="font-display font-black uppercase text-xl mt-1">
                Chat instantly
              </div>
            </div>
            <span className="mt-auto inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-[#FF0055] group-hover:gap-2 transition-all">
              Open WhatsApp <ArrowRight size={14} />
            </span>
          </a>
        </div>

        <p className="mt-10 text-center font-mono text-xs uppercase tracking-widest text-[#0D0D0D]/70">
          * Real human replies — usually within 24 hours.
        </p>
      </div>
    </section>
  );
}
